import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Lightning,
  Check,
  CaretRight,
  CaretLeft,
  Play,
  Stop,
} from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import EmptyDraw from "../ui/EmptyDraw";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./PendingFocusMode.css";

/**
 * Fullscreen "תצוגה ייעודית" (focus mode) for the pending-actions section.
 * Pure presentation layer — all data, handlers and per-card state live in
 * CollapsibleSections and arrive here as props, so behavior is identical
 * to the accordion. Desktop only (parent gates on isMobile).
 */
// Pure — hoisted so hooks that close over it don't need it as a dependency
const itemKey = (item) =>
  item.type === "live" ? `live-${item.data.id}` : item.data.id;

function PendingFocusMode({
  items,
  formatDate,
  formatTime,
  renderStagePanel,
  getAppointmentStages,
  getCardState,
  completingCards,
  endingLiveSessions,
  handleEnterLiveSession,
  handleEndLiveSession,
  isLoading,
  onClose,
}) {
  const [focusedId, setFocusedId] = useState(null);
  const overlayRef = useRef(null);
  const prevItemsRef = useRef(items);

  const focusedIndex = items.findIndex((it) => itemKey(it) === focusedId);

  // RTL: "next" moves toward the end of the list
  const focusRelative = useCallback(
    (delta) => {
      if (items.length === 0) return;
      const current = items.findIndex((it) => itemKey(it) === focusedId);
      const base = current === -1 ? 0 : current + delta;
      const clamped = Math.max(0, Math.min(items.length - 1, base));
      setFocusedId(itemKey(items[clamped]));
    },
    [items, focusedId]
  );

  // Body scroll lock while open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Capture keyboard on mount
  useEffect(() => {
    overlayRef.current?.focus();
  }, []);

  // Auto-advance: when the focused item leaves the list (completed / session
  // ended → query refetch drops it), focus the item now at the same position.
  useEffect(() => {
    const prevItems = prevItemsRef.current;
    prevItemsRef.current = items;
    if (!focusedId) return;
    const stillExists = items.some((it) => itemKey(it) === focusedId);
    if (stillExists) return;
    if (items.length === 0) {
      setFocusedId(null);
      return;
    }
    const prevIndex = prevItems.findIndex((it) => itemKey(it) === focusedId);
    const nextIndex = Math.max(0, Math.min(items.length - 1, prevIndex === -1 ? 0 : prevIndex));
    setFocusedId(itemKey(items[nextIndex]));
  }, [items, focusedId]);

  const handleKeyDown = (e) => {
    const tag = e.target?.tagName;
    const isTyping = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";

    if (e.key === "Escape") {
      e.stopPropagation();
      if (focusedId) setFocusedId(null);
      else onClose();
      return;
    }
    if (isTyping || !focusedId) return;
    // RTL: ArrowRight = previous item, ArrowLeft = next item
    if (e.key === "ArrowRight") {
      e.preventDefault();
      focusRelative(-1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusRelative(1);
    }
  };

  const renderLiveCard = (session) => {
    const isEnding = endingLiveSessions[session.id] === true;
    return (
      <div className="pfm-live-body">
        <span className="cs-pa-live-pill">
          <span className="cs-pa-live-dot" />
          סשן לייב פעיל
        </span>
        <div className="pfm-live-actions">
          <button
            type="button"
            className="cs-pa-btn-play"
            onClick={(e) => {
              e.stopPropagation();
              handleEnterLiveSession(session);
            }}
            title="המשך סשן"
            aria-label="המשך סשן"
          >
            <Play size={18} weight="fill" />
          </button>
          <button
            type="button"
            className="cs-pa-btn-stop"
            onClick={(e) => {
              e.stopPropagation();
              handleEndLiveSession(session);
            }}
            disabled={isEnding}
            title="סיום סשן"
            aria-label="סיום סשן"
          >
            {isEnding ? (
              <LoaderLoop size="xs" />
            ) : (
              <Stop size={18} weight="bold" />
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderCard = (item) => {
    const key = itemKey(item);
    const isFocused = focusedId === key;
    const isDimmed = focusedId !== null && !isFocused;
    const data = item.data;
    const isCompleting =
      item.type === "live"
        ? endingLiveSessions[data.id] === true
        : completingCards[data.id] === true;
    const stages = item.type === "appointment" ? getAppointmentStages(data) : [];

    return (
      <div
        key={key}
        role="listitem"
        className={`pfm-card ${isFocused ? "pfm-card-focused" : ""} ${
          isDimmed ? "pfm-card-dimmed" : ""
        } ${isCompleting ? "pfm-card-completing" : ""} ${
          item.type === "live" ? "pfm-card-live" : ""
        }`}
      >
        <button
          type="button"
          className="pfm-card-header"
          onClick={() => setFocusedId(isFocused ? null : key)}
          aria-expanded={isFocused}
        >
          <div className="pfm-card-info">
            <span className="pfm-card-name">{data.patientName}</span>
            <span className="pfm-card-time">
              {formatDate(item.time)} • {formatTime(item.time)}
            </span>
          </div>
          {item.type === "appointment" && (
            <div className="pfm-card-chips">
              {stages.map((s) => {
                const StageIcon = s.Icon;
                return (
                  <span
                    key={s.key}
                    className={`cs-pa-chip ${s.done ? "done" : "pending"}`}
                    data-stage={s.key}
                    title={s.done ? `${s.title} — הושלם` : `${s.title} — נדרש`}
                  >
                    {s.done ? (
                      <Check size={12} weight="bold" />
                    ) : (
                      <StageIcon size={12} weight="fill" />
                    )}
                    <span className="cs-pa-chip-label">{s.shortTitle}</span>
                  </span>
                );
              })}
            </div>
          )}
        </button>

        {item.type === "live" && renderLiveCard(data)}

        {item.type === "appointment" && isFocused && (
          <div className="pfm-card-body">
            {stages.map((s) => renderStagePanel(data, s, getCardState(data.id)))}
          </div>
        )}
      </div>
    );
  };

  const overlay = (
    <div
      className="pfm-overlay"
      ref={overlayRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="תצוגה ייעודית — פעולות ממתינות"
    >
      <div className="pfm-header">
        <div className="pfm-title">
          <Lightning size={22} weight="fill" />
          <span>פעולות ממתינות</span>
          {items.length > 0 && (
            <span className="pfm-count">{items.length}</span>
          )}
        </div>
        <ModalCloseButton onClick={onClose} className="pfm-close" />
      </div>

      {isLoading && items.length === 0 ? (
        <div className="pfm-empty">
          <LoaderLoop size="md" />
          <span>טוען...</span>
        </div>
      ) : items.length === 0 ? (
        <div className="pfm-empty">
          <EmptyDraw variant="done" />
          <span className="pfm-empty-title">כל הפעולות הושלמו!</span>
          <span className="pfm-empty-subtitle">אין פעולות ממתינות לעדכון</span>
        </div>
      ) : (
        <div
          className={`pfm-stage ${focusedId ? "pfm-stage-focused" : ""}`}
        >
          {focusedId && items.length > 1 && (
            <button
              type="button"
              className="pfm-nav-btn pfm-nav-prev"
              onClick={() => focusRelative(-1)}
              disabled={focusedIndex <= 0}
              aria-label="הקודם"
            >
              <CaretRight size={22} weight="bold" />
            </button>
          )}

          <div className="pfm-list" role="list">
            {items.map((item) => renderCard(item))}
          </div>

          {focusedId && items.length > 1 && (
            <button
              type="button"
              className="pfm-nav-btn pfm-nav-next"
              onClick={() => focusRelative(1)}
              disabled={focusedIndex >= items.length - 1}
              aria-label="הבא"
            >
              <CaretLeft size={22} weight="bold" />
            </button>
          )}
        </div>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}

export default PendingFocusMode;
